import { computed, unref } from 'vue'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import {
  createChannelSettings,
  fetchMessages,
  fetchUserChannels,
  fetchUserSettings,
  updateChannelSettings,
} from '@/backend/messageServiceApi'
import { syncWebPushSubscription } from '@/backend/webPushClient'
import {
  MESSAGE_CHANNELS_QUERY_KEY,
  MESSAGE_SETTINGS_QUERY_KEY,
  MESSAGES_LIST_QUERY_KEY,
} from '@/queryClient'
import {
  NOTIFICATION_VIEWS as VIEWS,
  buildChannelMessagesParams,
  buildPersonalMessagesParams,
  buildRoleMessagesParams,
  findSettingsForChannel,
  hasMessageFilterParams,
} from '@/components/notifications/notificationLogic.mjs'
import { useMarkMessageRead } from '@/composables/useMarkMessageRead'
import { useNotificationUser } from '@/composables/useNotificationUser'

/**
 * Общая загрузка inbox (колокольчик и страница уведомлений).
 * @param {{
 *   active: import('vue').MaybeRef<boolean>,
 *   view: import('vue').MaybeRef<string>,
 *   selectedChannel: import('vue').MaybeRef<object|null>,
 * }} opts
 */
export function useNotificationInbox(opts) {
  const { user, userRoleId, isAuthenticated, roleTitle } = useNotificationUser()
  const queryClient = useQueryClient()

  const isActive = computed(() => !!unref(opts.active))
  const view = computed(() => unref(opts.view))
  const selectedChannel = computed(() => unref(opts.selectedChannel) || null)

  const messagesParams = computed(() => {
    if (view.value === VIEWS.PERSONAL) {
      return buildPersonalMessagesParams(user.value?.id)
    }
    if (view.value === VIEWS.ROLE) {
      return buildRoleMessagesParams(userRoleId.value)
    }
    if (view.value === VIEWS.CHANNEL && selectedChannel.value?.id) {
      return buildChannelMessagesParams(selectedChannel.value.id)
    }
    return null
  })

  const messagesEnabled = computed(
    () => isActive.value
      && isAuthenticated.value
      && hasMessageFilterParams(messagesParams.value)
      && (view.value === VIEWS.PERSONAL || view.value === VIEWS.ROLE || view.value === VIEWS.CHANNEL),
  )

  const { markIfUnread } = useMarkMessageRead()

  const messagesQuery = useQuery({
    queryKey: computed(() => [MESSAGES_LIST_QUERY_KEY, messagesParams.value]),
    queryFn: async ({ signal }) => {
      const raw = await fetchMessages(messagesParams.value || {}, { signal })
      return Array.isArray(raw) ? raw : []
    },
    enabled: computed(() => messagesEnabled.value),
  })

  const messages = computed(() => messagesQuery.data.value || [])
  const messagesPending = computed(() => messagesQuery.isLoading.value)
  const messagesError = computed(() => messagesQuery.isError.value)

  const channelsEnabled = computed(
    () => isActive.value
      && isAuthenticated.value
      && (view.value === VIEWS.CHANNELS || view.value === VIEWS.CHANNEL),
  )

  const channelsQuery = useQuery({
    queryKey: [MESSAGE_CHANNELS_QUERY_KEY],
    queryFn: ({ signal }) => fetchUserChannels({}, { signal }),
    enabled: computed(() => channelsEnabled.value),
  })

  const channels = computed(() => channelsQuery.data.value || [])
  const channelsPending = computed(() => channelsQuery.isLoading.value)
  const channelsError = computed(() => channelsQuery.isError.value)

  const settingsEnabled = computed(
    () => isActive.value
      && isAuthenticated.value
      && (view.value === VIEWS.CHANNEL || view.value === VIEWS.CHANNELS),
  )

  const settingsQuery = useQuery({
    queryKey: [MESSAGE_SETTINGS_QUERY_KEY],
    queryFn: ({ signal }) => fetchUserSettings({ signal }),
    enabled: computed(() => settingsEnabled.value),
  })

  const channelSetting = computed(() =>
    findSettingsForChannel(settingsQuery.data.value || [], selectedChannel.value?.id),
  )

  const channelPushEnabled = computed(() => {
    const s = channelSetting.value
    if (!s) return true
    return !!s.is_send_push_message
  })

  const toggleMutation = useMutation({
    mutationFn: async (enabled) => {
      const channelId = selectedChannel.value?.id
      if (!channelId) throw new Error('channel not selected')
      const existing = findSettingsForChannel(settingsQuery.data.value || [], channelId)
      if (existing?.id) {
        return updateChannelSettings(existing.id, { is_send_push_message: enabled })
      }
      return createChannelSettings({
        channel_id: channelId,
        is_send_push_message: enabled,
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [MESSAGE_SETTINGS_QUERY_KEY] })
    },
  })

  const togglePending = computed(() => toggleMutation.isPending.value)

  const panelTitle = computed(() => {
    switch (view.value) {
      case VIEWS.PERSONAL:
        return 'Личные уведомления'
      case VIEWS.ROLE:
        return roleTitle.value
      case VIEWS.CHANNELS:
        return 'Каналы'
      case VIEWS.CHANNEL: {
        const ch = selectedChannel.value
        const fromList = channels.value.find((c) => Number(c.id) === Number(ch?.id))
        return fromList?.name || ch?.name || `Канал #${ch?.id || ''}`
      }
      default:
        return 'Уведомления'
    }
  })

  async function onToggleChannelPush(enabled) {
    try {
      await toggleMutation.mutateAsync(enabled)
      if (enabled) {
        await syncWebPushSubscription({ requestPermission: true })
      }
    } catch (err) {
      console.error('toggle channel push failed', err)
    }
  }

  return {
    user,
    userRoleId,
    isAuthenticated,
    roleTitle,
    messagesParams,
    messages,
    messagesPending,
    messagesError,
    channels,
    channelsPending,
    channelsError,
    channelPushEnabled,
    togglePending,
    panelTitle,
    onToggleChannelPush,
    markIfUnread,
    queryClient,
  }
}
